import { MARKET_IDS, POLICY_LIMIT_NAMES } from "@atc/domain";
import type { Line } from "./commands";

/**
 * One declaration per command, and everything that reads it.
 *
 * A terminal command used to be four things kept in four places — the parser
 * inside its handler, a `usage` string, a one-line `help` string and a flat
 * `keywords` list for Tab — and on 2026-09-10 they disagreed: `dca`'s help and
 * usage differed about its notional, its parser refused a `--max` the API
 * accepts, and Tab knew nothing about which flags belong to which subcommand.
 * `DESIGN-cli-command-spec` is the fix. A command declares its forms, arguments,
 * flags, examples and traps once, in `catalog.ts`, and this module turns that
 * declaration into the help index, the help page, the flag parser, Tab's
 * options and "did you mean".
 *
 * **Pure, like `complete.ts`.** Nothing here fetches or touches the DOM, so
 * every behaviour is testable from a string.
 */

export type ValueKind =
  | "symbol"
  | "money"
  | "shares"
  | "int"
  | "number"
  | "market"
  | "limit"
  | "tif"
  | "switch"
  | "setup"
  | "theme"
  | "command"
  | "feed"
  | "id"
  | "date"
  | "email"
  | "code"
  | "word"
  | "text";

export const HELP_GROUPS = [
  "getting started",
  "accounts & markets",
  "prices",
  "trading",
  "strategies",
  "agents",
  "risk & policy",
  "terminal",
] as const;
export type HelpGroup = (typeof HELP_GROUPS)[number];

/** The words a closed-set value may be, given what has been typed before it. */
export type Choices =
  | readonly string[]
  | ((typed: readonly string[]) => readonly string[] | undefined);

export type ArgSpec = {
  /** As it appears in a form's syntax, between angle brackets. */
  name: string;
  kind: ValueKind;
  about: string;
  choices?: Choices;
};

export type FlagSpec = {
  name: `--${string}`;
  /** The placeholder shown after it: `--interval <minutes>`. */
  value: string;
  kind: ValueKind;
  about: string;
  default?: string | number;
  min?: number;
  max?: number;
  /**
   * The subcommand it belongs to — `on` for `dca on` — or several. Absent means
   * every form.
   */
  for?: string | readonly string[];
  /** A closed set: Tab offers these, and any other value is refused before a request. */
  choices?: readonly string[];
  /** An open value's common answers: Tab offers these, and the API judges anything typed. */
  suggest?: readonly string[];
};

export type FormSpec = {
  /**
   * The command, literal words, `<arg>`, `<arg>...` for one or more, `a|b` for
   * alternatives, and `[ … ]` around anything optional. Flags are declared once
   * in `flags` and appended when a form is shown.
   */
  syntax: string;
  does: string;
  /** This form takes none of the command's flags — `replay stop`. */
  noFlags?: true;
};

export type ExampleSpec = {
  line: string;
  says: string;
  /** Answered from what the shell holds, so it never reaches the API. */
  local?: true;
};

export type CommandDoc = {
  group: HelpGroup;
  /** The one line the index lists. */
  summary: string;
  forms: readonly FormSpec[];
  args?: readonly ArgSpec[];
  flags?: readonly FlagSpec[];
  examples: readonly ExampleSpec[];
  traps?: readonly string[];
  seeAlso?: readonly string[];
};

export const TIME_IN_FORCE = ["day", "gtc", "ioc", "fok"] as const;

const note = (text: string): Line => ({ kind: "note", text });
const out = (text: string): Line => ({ kind: "out", text });
const row = (text: string): Line => ({ kind: "out", text, pre: true });
const rowNote = (text: string): Line => ({ kind: "note", text, pre: true });
const pad = (s: string, n: number) => (s.length >= n ? `${s}  ` : s.padEnd(n));

/* ── syntax ────────────────────────────────────────────────────────────────── */

type SyntaxNode =
  | { t: "lit"; words: readonly string[] }
  | { t: "arg"; name: string; many: boolean }
  | { t: "open"; close: number }
  | { t: "close" };

/**
 * A form's syntax as a small automaton: `[` jumps past its `]` or steps in,
 * and `<arg>...` may consume again. Throws on a syntax it cannot read, which
 * the catalogue test turns into a failing declaration rather than a Tab that
 * silently offers nothing.
 */
export function compileSyntax(syntax: string): SyntaxNode[] {
  const nodes: SyntaxNode[] = [];
  const open: number[] = [];
  for (const raw of syntax.trim().split(/\s+/)) {
    let token = raw;
    while (token.startsWith("[")) {
      open.push(nodes.length);
      nodes.push({ t: "open", close: -1 });
      token = token.slice(1);
    }
    let closes = 0;
    while (token.endsWith("]")) {
      closes++;
      token = token.slice(0, -1);
    }
    if (token) {
      const arg = /^<([a-z][a-z0-9-]*)>(\.\.\.)?$/.exec(token);
      if (arg) nodes.push({ t: "arg", name: arg[1] as string, many: arg[2] !== undefined });
      else if (/^[a-z0-9_-]+(\|[a-z0-9_-]+)*$/.test(token))
        nodes.push({ t: "lit", words: token.split("|") });
      else throw new Error(`cannot read "${raw}" in the syntax "${syntax}"`);
    }
    for (; closes > 0; closes--) {
      const at = open.pop();
      if (at === undefined) throw new Error(`unmatched ] in the syntax "${syntax}"`);
      (nodes[at] as { t: "open"; close: number }).close = nodes.length;
      nodes.push({ t: "close" });
    }
  }
  if (open.length > 0) throw new Error(`unclosed [ in the syntax "${syntax}"`);
  return nodes;
}

function closure(nodes: readonly SyntaxNode[], from: Iterable<number>): Set<number> {
  const reached = new Set<number>();
  const stack = [...from];
  while (stack.length > 0) {
    const i = stack.pop() as number;
    if (reached.has(i)) continue;
    reached.add(i);
    const node = nodes[i];
    if (node?.t === "open") stack.push(i + 1, node.close + 1);
    else if (node?.t === "close") stack.push(i + 1);
  }
  return reached;
}

/** A literal word wins over a free argument at the same position. */
function advance(nodes: readonly SyntaxNode[], states: Set<number>, token: string): Set<number> {
  const word = token.toLowerCase();
  const literal: number[] = [];
  const free: number[] = [];
  for (const i of states) {
    const node = nodes[i];
    if (node?.t === "lit" && node.words.includes(word)) literal.push(i + 1);
    if (node?.t === "arg") {
      free.push(i + 1);
      if (node.many) free.push(i);
    }
  }
  return closure(nodes, literal.length > 0 ? literal : free);
}

/** The subcommand a form is about — `on` in `dca on <symbol>` — if it has one. */
function subcommandOf(form: FormSpec): readonly string[] {
  const second = compileSyntax(form.syntax)[1];
  return second?.t === "lit" ? second.words : [];
}

/** The subcommands a flag is scoped to, or none when it belongs to every form. */
export const flagScope = (flag: FlagSpec): readonly string[] =>
  flag.for === undefined ? [] : typeof flag.for === "string" ? [flag.for] : flag.for;

export function flagsFor(doc: CommandDoc, subcommand: string | undefined): readonly FlagSpec[] {
  return (doc.flags ?? []).filter(
    (f) => f.for === undefined || (subcommand !== undefined && flagScope(f).includes(subcommand)),
  );
}

/* ── completion ────────────────────────────────────────────────────────────── */

/** Closed sets only the shell holds, handed in so this module never asks for them. */
export type ShellSets = {
  setups: readonly string[];
  themes: readonly string[];
  watched: readonly string[];
  commands: readonly string[];
  /** Feeds the trader may choose, as `GET /api/v1/feeds` last listed them. */
  feeds: readonly string[];
};

export type Options = { words: readonly string[]; hint?: string };

function closedValues(
  kind: ValueKind,
  choices: Choices | undefined,
  typed: readonly string[],
  shell: ShellSets,
): readonly string[] | undefined {
  if (typeof choices === "function") return choices(typed);
  if (choices) return choices;
  switch (kind) {
    case "market":
      return MARKET_IDS;
    case "limit":
      return POLICY_LIMIT_NAMES;
    case "tif":
      return TIME_IN_FORCE;
    case "switch":
      return ["on", "off"];
    case "setup":
      return shell.setups;
    case "theme":
      return shell.themes;
    case "command":
      return shell.commands;
    case "symbol":
      return shell.watched.length > 0 ? shell.watched : undefined;
    case "feed":
      return shell.feeds.length > 0 ? shell.feeds : undefined;
    default:
      return undefined;
  }
}

const argHint = (arg: ArgSpec) => `<${arg.name}>  ${arg.about}`;
const flagHint = (flag: FlagSpec) => `${flag.name} <${flag.value}>  ${describeFlag(flag)}`;

/**
 * What may come next after a command and the tokens typed so far.
 *
 * `words` are what Tab completes to; `hint` is the one line it prints when the
 * value is free — a price, a quantity, an order id — and nothing can be
 * completed. Silence is indistinguishable from a broken key.
 */
export function optionsFor(
  doc: CommandDoc,
  typed: readonly string[],
  partial: string,
  shell: ShellSets,
): Options {
  const flags = doc.flags ?? [];
  const positional: string[] = [];
  const used = new Set<string>();
  let awaiting: FlagSpec | undefined;
  for (let i = 0; i < typed.length; i++) {
    const token = typed[i] as string;
    if (!token.startsWith("--")) {
      positional.push(token);
      continue;
    }
    const [name = ""] = token.split("=");
    used.add(name);
    const flag = flags.find((f) => f.name === name);
    if (flag && !token.includes("=")) {
      if (i + 1 < typed.length) i++;
      else awaiting = flag;
    }
  }

  if (awaiting) {
    const values = closedValues(
      awaiting.kind,
      awaiting.choices ?? awaiting.suggest,
      positional,
      shell,
    );
    return { words: values ?? [], hint: flagHint(awaiting) };
  }

  const words = new Set<string>();
  const hints: string[] = [];
  const offeredFlags = new Set<string>();
  for (const form of doc.forms) {
    // Indices stay whole: an `[` records where its `]` is, so dropping the
    // command's own word would send every skip one node too far.
    const nodes = compileSyntax(form.syntax);
    let states = closure(nodes, [1]);
    for (const token of positional) states = advance(nodes, states, token);
    if (states.size === 0) continue;

    for (const i of states) {
      const node = nodes[i];
      if (node?.t === "lit") for (const w of node.words) words.add(w);
      if (node?.t === "arg") {
        const arg = doc.args?.find((a) => a.name === node.name);
        if (!arg) continue;
        for (const w of closedValues(arg.kind, arg.choices, positional, shell) ?? []) words.add(w);
        hints.push(argHint(arg));
      }
    }

    const sub = subcommandOf(form);
    const typedSub = positional[0]?.toLowerCase();
    if (!form.noFlags && (sub.length === 0 || (typedSub !== undefined && sub.includes(typedSub)))) {
      for (const f of flagsFor(doc, sub.length > 0 ? typedSub : undefined)) {
        if (!used.has(f.name)) offeredFlags.add(f.name);
      }
    }
  }

  if (partial.startsWith("-") || words.size === 0) for (const f of offeredFlags) words.add(f);
  const hint = hints[0];
  return hint === undefined ? { words: [...words] } : { words: [...words], hint };
}

/* ── the flag parser ───────────────────────────────────────────────────────── */

export type ParsedFlags = { positional: string[]; values: Record<string, string> };

function describeFlag(flag: FlagSpec): string {
  const parts = [flag.about];
  if (flag.default !== undefined) parts.push(`default ${flag.default}`);
  if (flag.min !== undefined && flag.max !== undefined) parts.push(`${flag.min}–${flag.max}`);
  return parts.join(" · ");
}

function checkValue(flag: FlagSpec, value: string): string | undefined {
  const range = flag.min !== undefined && flag.max !== undefined ? `, ${flag.min}–${flag.max}` : "";
  if (flag.choices && !flag.choices.includes(value))
    return `${flag.name} takes ${flag.choices.join(" | ")}, not ${value}`;
  if (flag.kind === "int") {
    if (!/^\d+$/.test(value)) return `${flag.name} takes a whole number${range}`;
  } else if (flag.kind === "money" || flag.kind === "shares" || flag.kind === "number") {
    if (!/^\d+(\.\d+)?$/.test(value)) return `${flag.name} takes an amount like 100${range}`;
  } else return undefined;
  const n = Number(value);
  if ((flag.min !== undefined && n < flag.min) || (flag.max !== undefined && n > flag.max)) {
    return `${flag.name} must be ${flag.min}–${flag.max}, not ${value}`;
  }
  return undefined;
}

/**
 * `--flag value` and `--flag=value`, checked against the declaration before any
 * request is made. An undeclared or repeated flag is refused by naming the
 * valid ones — a typo silently ignored would configure something other than
 * what was typed and report success.
 */
export function parseFlags(
  tokens: readonly string[],
  flags: readonly FlagSpec[],
  what: string,
): ParsedFlags | { error: string } {
  const positional: string[] = [];
  const values: Record<string, string> = {};
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i] as string;
    if (!token.startsWith("--")) {
      positional.push(token);
      continue;
    }
    const eq = token.indexOf("=");
    const name = eq === -1 ? token : token.slice(0, eq);
    const flag = flags.find((f) => f.name === name);
    if (!flag) {
      return {
        error:
          flags.length > 0
            ? `${what} does not take ${name} — it takes ${flags.map((f) => f.name).join(", ")}`
            : `${what} takes no flags`,
      };
    }
    if (Object.hasOwn(values, name)) return { error: `${name} is given twice` };
    const value = eq === -1 ? tokens[++i] : token.slice(eq + 1);
    if (value === undefined || value === "" || value.startsWith("--")) {
      return { error: `${name} needs <${flag.value}>` };
    }
    const problem = checkValue(flag, value);
    if (problem) return { error: problem };
    values[name] = value;
  }
  return { positional, values };
}

/* ── did you mean ──────────────────────────────────────────────────────────── */

function distance(a: string, b: string): number {
  let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      current[j] = Math.min(
        (previous[j] as number) + 1,
        (current[j - 1] as number) + 1,
        (previous[j - 1] as number) + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    previous = current;
  }
  return previous[b.length] as number;
}

/**
 * Every command the typed word could have meant: each one it begins, and each
 * one within two edits. All of them, never the first — which one `find` picked
 * depended on declaration order. Edit distance waits for three characters,
 * because two edits from a two-letter word is most of the command list.
 */
export function suggest(typed: string, names: readonly string[]): string[] {
  const word = typed.toLowerCase();
  if (!word) return [];
  const prefixed = names.filter((n) => n.startsWith(word));
  const near = word.length >= 3 ? names.filter((n) => distance(word, n) <= 2) : [];
  return [...new Set([...prefixed, ...near])].sort();
}

/* ── help ──────────────────────────────────────────────────────────────────── */

/** A form as shown: its syntax, then the flags that belong to it. */
export function formLine(doc: CommandDoc, form: FormSpec): string {
  const sub = subcommandOf(form);
  const flags = (form.noFlags ? [] : (doc.flags ?? [])).filter(
    (f) => f.for === undefined || flagScope(f).some((w) => sub.includes(w)),
  );
  return [form.syntax, ...flags.map((f) => `[${f.name} <${f.value}>]`)].join(" ");
}

export function usageOf(doc: CommandDoc): string {
  return doc.forms.map((form) => formLine(doc, form)).join("  |  ");
}

export function renderHelpIndex(docs: Readonly<Record<string, CommandDoc>>): Line[] {
  const lines: Line[] = [];
  const names = Object.keys(docs).sort();
  for (const group of HELP_GROUPS) {
    const inGroup = names.filter((n) => docs[n]?.group === group);
    if (inGroup.length === 0) continue;
    lines.push(note(group));
    for (const name of inGroup) {
      lines.push(row(`  ${pad(name, 11)}${(docs[name] as CommandDoc).summary}`));
    }
  }
  lines.push(
    note(""),
    note("`help <command>` explains one: its forms, flags, defaults, examples and traps."),
    note("this is a paper trading terminal — no real money moves"),
  );
  return lines;
}

export function renderHelpPage(name: string, doc: CommandDoc): Line[] {
  const lines: Line[] = [out(`${name} — ${doc.summary}`), note(""), note("usage")];
  for (const form of doc.forms) {
    lines.push(row(`  ${formLine(doc, form)}`), note(`      ${form.does}`));
  }

  if (doc.args && doc.args.length > 0) {
    lines.push(note(""), note("arguments"));
    const width = Math.max(...doc.args.map((a) => a.name.length + 2)) + 3;
    for (const arg of doc.args) {
      lines.push(rowNote(`  ${pad(`<${arg.name}>`, width)}${arg.about}`));
    }
  }

  if (doc.flags && doc.flags.length > 0) {
    lines.push(note(""), note("flags"));
    const labels = doc.flags.map((f) => `${f.name} <${f.value}>`);
    const width = Math.max(...labels.map((l) => l.length)) + 3;
    doc.flags.forEach((flag, i) => {
      const scope = flag.for
        ? ` · ${flagScope(flag)
            .map((w) => `${name} ${w}`)
            .join(", ")}`
        : "";
      lines.push(rowNote(`  ${pad(labels[i] as string, width)}${describeFlag(flag)}${scope}`));
    });
  }

  lines.push(note(""), note("examples"));
  for (const example of doc.examples) {
    lines.push(row(`  ${example.line}`), note(`      ${example.says}`));
  }

  if (doc.traps && doc.traps.length > 0) {
    lines.push(note(""), note("traps"));
    for (const trap of doc.traps) lines.push(note(`  ! ${trap}`));
  }

  if (doc.seeAlso && doc.seeAlso.length > 0) {
    lines.push(note(""), note(`see also  ${doc.seeAlso.join(", ")}`));
  }
  return lines;
}
